import React from "react";
import { cn } from "../lib/helpers";

// Tabbed Results Component for JSON arrays
export const TabbedResults = ({ results, title }) => {
  const [activeTab, setActiveTab] = React.useState(0);

  if (!results) return null;

  let resultsArray;
  try {
    resultsArray = typeof results === 'string' ? JSON.parse(results) : results;
  } catch (e) {
    // If it's not valid JSON, treat as single item
    resultsArray = [results];
  }

  if (!Array.isArray(resultsArray)) {
    resultsArray = [resultsArray];
  }

  if (resultsArray.length === 0) return null;

  // Helper function to format keys from snake_case to Title Case
  const formatKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Function to parse and highlight variables in the format { variable name }
  const parsePromptWithVariables = (text) => {
    if (!text || typeof text !== 'string') {
      return text;
    }

    const variableRegex = /\{\s*([^}]+)\s*\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = variableRegex.exec(text)) !== null) {
      // Add text before the variable
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      // Add the highlighted variable
      parts.push(
        <span
          key={`var-${match.index}`}
          className="bg-light-5 text-melon px-1 py-0.5 rounded text-sm font-medium"
        >
          {match[0]}
        </span>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after the last variable
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  // Pill component
  const Pill = ({ children, className }) => (
    <div className={cn(
      "px-3 py-1.5 bg-dark-5 rounded-xl",
      className
    )}>
      {children}
    </div>
  );

  const renderOutputPills = (output) => {
    if (!output) return null;

    return Object.entries(output).map(([key, value]) => {
      if (Array.isArray(value)) {
        return (
          <div key={key} className="mt-2 border border-black rounded-lg bg-dark-2 p-4">
            <h5 className="font-medium mb-2">{formatKey(key)}</h5>
            <div className="flex flex-wrap gap-2">
              {value.map((item, index) => (
                <Pill key={index}>{parsePromptWithVariables(String(item))}</Pill>
              ))}
            </div>
          </div>
        );
      } else {
        return (
          <Pill
            className="mb-2 mr-4 inline-flex text-lg xs:bg-white text-black xs:px-4 xs:py-4 font-medium"
            key={key}>
            {parsePromptWithVariables(String(value))}
          </Pill>
        );
      }
    });
  };

  const renderKeyValueTable = (data) => {
    if (!data) return null;

    const otherProps = Object.entries(data).filter(([key]) => key !== 'output');

    if (otherProps.length === 0) return null;

    return (
      <div className="border border-dark-10 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          {/* <thead className="bg-dark-20">
            <tr className="font-medium text-left">
              <th className="px-4 py-3">Prompt Variables</th>
              <th className="px-4 py-3">Value</th>
            </tr>
          </thead> */}
          <tbody>
            {otherProps.map(([key, value], index) => (
              <tr key={key} className={index % 2 === 0 ? 'bg-dark-5' : 'bg-light-50'}>
                <td className="px-4 py-3 font-medium">{formatKey(key)}</td>
                <td className="px-4 py-3">{parsePromptWithVariables(String(value))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="mt-6">
      {/* Title (for scenario-prompt pages) */}
      {title && (
        <h3 className="text-2xl font-semibold mb-6">
          {title}
        </h3>
      )}

      {/* Tab Navigation */}
      {resultsArray.length > 1 && (
        <div className="flex gap-2 mb-4">
          {resultsArray.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`px-4 py-2 rounded-lg text-sm font-medium outline-none focus:outline-none focus:ring-0 transition-colors ${activeTab === index
                ? 'bg-green-emerald text-white'
                : 'bg-dark-5 text-secondary hover:bg-dark-10 hover:text-green-emerald'
                }`}
            >
              Result {index + 1}
            </button>
          ))}
        </div>
      )}

      {/* Tab Content */}
      <div className="bg-light-20 border border-green-emerald rounded-xl p-3">
        {resultsArray[activeTab]?.output && renderOutputPills(resultsArray[activeTab].output)}
        <p className="font-medium text-primary my-2 mt-8">
          Variables used...
        </p>
        <div>
          {renderKeyValueTable(resultsArray[activeTab])}
        </div>
      </div>
    </div>
  );
};
